import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { fetchUsers, createUser, updateUser, deactivateUser, deleteUser } from '../../store/slices/usersSlice';
import { fetchDepartments, createDepartment, updateDepartment, deleteDepartment } from '../../store/slices/departmentsSlice';
import { fetchCompanies, createCompany, updateCompany } from '../../store/slices/companiesSlice';
import {
  fetchGroups, fetchGroup, createGroup, updateGroup,
  deleteGroup, addGroupMembers, removeGroupMember, clearSelected,
} from '../../store/slices/groupsSlice';
import { positionsAPI, titlesAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { groupName, groupDesc } from '../../utils/userGroupDisplay';
import PageHeader from '../../components/PageHeader/PageHeader';
import SearchableSelect from '../../components/SearchableSelect';
import './HRPanel.scss';

// ═══════════════════════════════════════════════════════════════════════════
// Constants
// ═══════════════════════════════════════════════════════════════════════════
const ROLE_COLORS = {
  tenant_owner: '#7c3aed', tenant_admin: '#dc2626', legal_manager: '#2563eb',
  contract_manager: '#0891b2', approver: '#d97706', reviewer: '#059669',
  employee: '#6b7280', external_user: '#9ca3af',
};
const ALLOWED_ROLES = ['tenant_admin', 'legal_manager', 'contract_manager', 'approver', 'reviewer', 'employee', 'external_user'];
const DEPT_PRESET_COLORS = ['#6366f1', '#0891b2', '#059669', '#d97706', '#dc2626', '#7c3aed', '#db2777', '#0284c7', '#64748b'];

// ═══════════════════════════════════════════════════════════════════════════
// SVG Icons (no emoji — per UI/UX rule)
// ═══════════════════════════════════════════════════════════════════════════
const Icons = {
  search: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>,
  plus: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  x: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  edit: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  trash: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  users: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  building: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M12 6h.01"/><path d="M12 10h.01"/><path d="M12 14h.01"/><path d="M16 10h.01"/><path d="M16 14h.01"/><path d="M8 10h.01"/><path d="M8 14h.01"/></svg>,
  briefcase: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>,
  badge: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.24 12.24a6 6 0 0 0-8.49-8.49L5 10.5V19h8.5z"/><line x1="16" y1="8" x2="2" y2="22"/><line x1="17.5" y1="15" x2="9" y2="15"/></svg>,
  user: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  team: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  filter: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>,
  palette: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="13.5" cy="6.5" r=".5" fill="currentColor"/><circle cx="17.5" cy="10.5" r=".5" fill="currentColor"/><circle cx="8.5" cy="7.5" r=".5" fill="currentColor"/><circle cx="6.5" cy="12.5" r=".5" fill="currentColor"/><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/></svg>,
  shield: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  mail: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
  phone: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
  chevronRight: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>,
  alert: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  info: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>,
  dot: <svg width="6" height="6" viewBox="0 0 6 6" fill="currentColor"><circle cx="3" cy="3" r="3"/></svg>,
  sparkle: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v3m0 12v3M3 12h3m12 0h3M5.6 5.6l2.1 2.1m8.6 8.6l2.1 2.1M5.6 18.4l2.1-2.1m8.6-8.6l2.1-2.1"/></svg>,
  hash: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></svg>,
  download: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
  company: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="22" x2="21" y2="22"/><line x1="6" y1="18" x2="6" y2="11"/><line x1="10" y1="18" x2="10" y2="11"/><line x1="14" y1="18" x2="14" y2="11"/><line x1="18" y1="18" x2="18" y2="11"/><polygon points="12 2 20 7 4 7"/></svg>,
};

// Extract a company id whether the field is populated ({ _id, name }) or raw.
const companyIdOf = (rec) => String(rec?.companyId?._id || rec?.companyId || '');
// Org record belongs to the given company (no company filter = everything;
// legacy records without a company stay visible everywhere).
const matchesCompany = (rec, cid) => !cid || !companyIdOf(rec) || companyIdOf(rec) === String(cid);

// ═══════════════════════════════════════════════════════════════════════════
// Avatar
// ═══════════════════════════════════════════════════════════════════════════
function Avatar({ user, size = 36 }) {
  const initials = `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase();
  const color = ROLE_COLORS[user.role] || '#6366f1';
  return (
    <div className="hr-avatar" style={{ width: size, height: size, background: color, fontSize: size * 0.36 }}>
      {user.avatarUrl ? <img src={user.avatarUrl} alt={initials} /> : initials}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// User Detail Side Drawer
// ═══════════════════════════════════════════════════════════════════════════
function UserDrawer({ user, users, departments, positions, titles, companies, onClose, onSave, onRequestDelete, canDelete }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    email: user.email || '',
    phone: user.phone || '',
    companyId: user.companyId?._id || user.companyId || '',
    positionRef: user.positionRef?._id || user.positionRef || '',
    titleRef: user.titleRef?._id || user.titleRef || '',
    departmentRef: user.departmentRef?._id || user.departmentRef || '',
    manager: user.manager?._id || user.manager || '',
    isActive: user.isActive !== false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Company drives the org structure: switching it clears department,
    // position, and title so no selection from the old company survives.
    if (name === 'companyId') {
      setForm(f => ({ ...f, companyId: value, departmentRef: '', positionRef: '', titleRef: '' }));
      return;
    }
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };
  // SearchableSelect hands back the bare value — wrap it in the event shape
  // handleChange expects so the company-switch reset keeps working.
  const selChange = (name) => (v) => handleChange({ target: { name, value: v } });

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setSaving(true); setError('');
    try { await onSave(user._id, form); onClose(); }
    catch (err) { setError(err?.response?.data?.message || err?.response?.data?.error || err); }
    finally { setSaving(false); }
  };

  const otherUsers = users.filter(u => u._id !== user._id && u.isActive);
  const roleColor = ROLE_COLORS[user.role] || '#6366f1';

  // Only the selected company's org structure is offered.
  const companyDepartments = departments.filter(d => matchesCompany(d, form.companyId));
  const companyPositions = positions.filter(p => matchesCompany(p, form.companyId));
  const companyTitles = titles.filter(ti => matchesCompany(ti, form.companyId));

  const selectedPosition = positions.find(p => p._id === form.positionRef);
  const positionOccupied = selectedPosition?.assignedUser &&
    (selectedPosition.assignedUser._id || selectedPosition.assignedUser) !== user._id &&
    (selectedPosition.assignedUser._id || selectedPosition.assignedUser) !== '';

  return (
    <>
      <div className="hr-drawer-overlay" onClick={onClose} />
      <div className="hr-drawer" role="dialog" aria-modal="true" aria-label={t('hrPanel.userProfile')}>
        {/* Gradient cover header */}
        <div className="hr-drawer__cover" style={{ '--role-color': roleColor }}>
          <button className="hr-drawer__close" onClick={onClose} aria-label={t('hrPanel.cancel')}>{Icons.x}</button>
          <div className="hr-drawer__identity">
            <div className="hr-drawer__avatar" style={{ background: roleColor }}>
              {user.avatarUrl
                ? <img src={user.avatarUrl} alt="" />
                : `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase()}
            </div>
            <div className="hr-drawer__meta">
              <h3 className="hr-drawer__name">{user.firstName} {user.lastName}</h3>
              <a className="hr-drawer__email" href={`mailto:${user.email}`}>{Icons.mail} {user.email}</a>
              <div className="hr-drawer__badges">
                <span className="hr-chip" style={{ color: roleColor, background: roleColor + '15' }}>
                  {Icons.shield}
                  {t('users.roles.' + user.role)}
                </span>
                <span className={`hr-chip hr-chip--${user.isActive ? 'success' : 'muted'}`}>
                  <span className="hr-chip__dot" />
                  {user.isActive ? t('hrPanel.activeStatus') : t('hrPanel.inactiveStatus')}
                </span>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="hr-drawer__body">
          {error && (
            <div className="hr-alert hr-alert--error" role="alert">
              {Icons.alert}
              <span>{error}</span>
            </div>
          )}

          <div className="hr-section">
            <div className="hr-section__title">{Icons.user} {t('hrPanel.basicInformation')}</div>
            <div className="hr-form-grid">
              <div className="hr-field">
                <label className="hr-label" htmlFor="u-firstName">{t('hrPanel.firstName')}</label>
                <input id="u-firstName" className="hr-input" name="firstName" value={form.firstName} onChange={handleChange} required />
              </div>
              <div className="hr-field">
                <label className="hr-label" htmlFor="u-lastName">{t('hrPanel.lastName')}</label>
                <input id="u-lastName" className="hr-input" name="lastName" value={form.lastName} onChange={handleChange} required />
              </div>
              <div className="hr-field hr-field--full">
                <label className="hr-label" htmlFor="u-phone">{t('hrPanel.phone')}</label>
                <div className="hr-input-wrap">
                  <span className="hr-input-wrap__icon">{Icons.phone}</span>
                  <input id="u-phone" className="hr-input hr-input--with-icon" name="phone" value={form.phone} onChange={handleChange} placeholder="+1 555 000 0000" />
                </div>
              </div>
            </div>
          </div>

          <div className="hr-section">
            <div className="hr-section__title">{Icons.briefcase} {t('hrPanel.workAssignment')}</div>
            <div className="hr-form-grid">
              <div className="hr-field hr-field--full">
                <label className="hr-label" htmlFor="u-company">{t('hrPanel.companyRequired')}</label>
                <SearchableSelect
                  id="u-company"
                  value={form.companyId}
                  onChange={selChange('companyId')}
                  placeholder={t('hrPanel.selectCompany')}
                  required
                  options={(companies || []).filter(c => c.isActive !== false || c._id === form.companyId).map(c => ({ value: c._id, label: c.name }))}
                />
                <span className="hr-field__hint">{Icons.info} {t('hrPanel.companyDrivesOrg')}</span>
              </div>
              <div className="hr-field">
                <label className="hr-label" htmlFor="u-position">{t('hrPanel.position')}</label>
                <SearchableSelect
                  id="u-position"
                  value={form.positionRef}
                  onChange={selChange('positionRef')}
                  options={[
                    { value: '', label: t('hrPanel.noneOption') },
                    ...companyPositions.map(p => ({ value: p._id, label: p.name, sublabel: p.code })),
                  ]}
                />
                {positionOccupied && (
                  <span className="hr-field__hint hr-field__hint--warn">
                    {Icons.info} {t('hrPanel.positionOccupied')}
                  </span>
                )}
              </div>
              <div className="hr-field">
                <label className="hr-label" htmlFor="u-title">{t('hrPanel.titleLabel')}</label>
                <SearchableSelect
                  id="u-title"
                  value={form.titleRef}
                  onChange={selChange('titleRef')}
                  options={[
                    { value: '', label: t('hrPanel.noneOption') },
                    ...companyTitles.map(ti => ({ value: ti._id, label: ti.name, sublabel: ti.code })),
                  ]}
                />
              </div>
              <div className="hr-field">
                <label className="hr-label" htmlFor="u-dept">{t('hrPanel.department')}</label>
                <SearchableSelect
                  id="u-dept"
                  value={form.departmentRef}
                  onChange={selChange('departmentRef')}
                  options={[
                    { value: '', label: t('hrPanel.noneOption') },
                    ...companyDepartments.map(d => ({ value: d._id, label: d.name })),
                  ]}
                />
              </div>
              <div className="hr-field">
                <label className="hr-label" htmlFor="u-manager">{t('hrPanel.manager')}</label>
                <SearchableSelect
                  id="u-manager"
                  value={form.manager}
                  onChange={selChange('manager')}
                  options={[
                    { value: '', label: t('hrPanel.noneOption') },
                    ...otherUsers.map(u => ({ value: u._id, label: `${u.firstName} ${u.lastName}` })),
                  ]}
                />
              </div>
            </div>
          </div>

          <div className="hr-section">
            <div className="hr-section__title">{Icons.shield} {t('hrPanel.accountAccess')}</div>
            <label className="hr-toggle-row" htmlFor="toggle-active">
              <div className="hr-toggle-row__info">
                <span className="hr-toggle-row__label">{t('hrPanel.activeAccount')}</span>
                <span className="hr-toggle-row__hint">{t('hrPanel.activeAccountHint')}</span>
              </div>
              <span className="hr-toggle">
                <input
                  id="toggle-active"
                  type="checkbox"
                  name="isActive"
                  checked={form.isActive}
                  onChange={handleChange}
                  disabled={user.role === 'tenant_owner'}
                />
                <span className="hr-toggle__track" />
              </span>
            </label>
          </div>
        </form>

        <div className="hr-drawer__footer">
          {canDelete ? (
            <button type="button" className="hr-btn hr-btn--danger-ghost" onClick={onRequestDelete}>
              {Icons.trash} {t('hrPanel.deleteUser')}
            </button>
          ) : (
            <span className="hr-drawer__hint"><kbd>Esc</kbd> {t('hrPanel.toClose')}</span>
          )}
          <div className="hr-drawer__actions">
            <button type="button" className="hr-btn hr-btn--ghost" onClick={onClose}>{t('hrPanel.cancel')}</button>
            <button type="button" className="hr-btn hr-btn--primary" disabled={saving} onClick={handleSubmit}>
              {saving ? t('hrPanel.saving') : t('hrPanel.saveChanges')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Invite User Modal
// ═══════════════════════════════════════════════════════════════════════════
function InviteUserModal({ departments, titles, companies, onClose, onSave }) {
  const { t } = useTranslation();
  const authUser = useSelector(s => s.auth.user);
  // Default to the operator's own company — most invitees join it.
  const defaultCompanyId = authUser?.companyId?._id || authUser?.companyId || '';
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', companyId: defaultCompanyId, titleRef: '', departmentRef: '' });
  // 'invite' → temp password emailed to the user; 'manual' → operator types the password here
  const [mode, setMode] = useState('invite');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [requireChange, setRequireChange] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isManual = mode === 'manual';
  const handleChange = (e) => {
    const { name, value } = e.target;
    // Switching company clears the org selections of the old company.
    if (name === 'companyId') {
      setForm(f => ({ ...f, companyId: value, titleRef: '', departmentRef: '' }));
      return;
    }
    setForm(f => ({ ...f, [name]: value }));
  };
  const selChange = (name) => (v) => handleChange({ target: { name, value: v } });

  const companyDepartments = departments.filter(d => matchesCompany(d, form.companyId));
  const companyTitles = titles.filter(ti => matchesCompany(ti, form.companyId));

  const validatePassword = () => {
    if (password.length < 8) return t('hrPanel.passwordTooShort');
    if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/\d/.test(password)) return t('hrPanel.passwordWeak');
    if (password !== confirmPassword) return t('hrPanel.passwordMismatch');
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let payload = form;
    if (isManual) {
      const pwError = validatePassword();
      if (pwError) { setError(pwError); return; }
      payload = { ...form, password, requirePasswordChange: requireChange };
    }
    setSaving(true); setError('');
    try { await onSave(payload); onClose(); }
    catch (err) { setError(err); }
    finally { setSaving(false); }
  };

  return (
    <div className="hr-modal-overlay" onClick={onClose}>
      <div className="hr-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="hr-modal__header hr-modal__header--accent">
          <div className="hr-modal__header-icon">{Icons.users}</div>
          <div className="hr-modal__header-text">
            <h2>{t('hrPanel.inviteNewUser')}</h2>
            <p>{t('hrPanel.inviteNewUserHint')}</p>
          </div>
          <button className="hr-modal__close" onClick={onClose} aria-label={t('hrPanel.cancel')}>{Icons.x}</button>
        </div>
        <form onSubmit={handleSubmit} className="hr-modal__body">
          {error && (
            <div className="hr-alert hr-alert--error" role="alert">
              {Icons.alert}<span>{error}</span>
            </div>
          )}
          <div className="hr-form-grid">
            <div className="hr-field">
              <label className="hr-label">{t('hrPanel.firstNameRequired')}</label>
              <input className="hr-input" name="firstName" value={form.firstName} onChange={handleChange} required />
            </div>
            <div className="hr-field">
              <label className="hr-label">{t('hrPanel.lastNameRequired')}</label>
              <input className="hr-input" name="lastName" value={form.lastName} onChange={handleChange} required />
            </div>
            <div className="hr-field hr-field--full">
              <label className="hr-label">{t('hrPanel.emailRequired')}</label>
              <div className="hr-input-wrap">
                <span className="hr-input-wrap__icon">{Icons.mail}</span>
                <input className="hr-input hr-input--with-icon" type="email" name="email" value={form.email} onChange={handleChange} required autoComplete="email" />
              </div>
            </div>
            <div className="hr-field hr-field--full">
              <label className="hr-label">{t('hrPanel.companyRequired')}</label>
              <SearchableSelect
                value={form.companyId}
                onChange={selChange('companyId')}
                placeholder={t('hrPanel.selectCompany')}
                required
                options={(companies || []).filter(c => c.isActive !== false).map(c => ({ value: c._id, label: c.name }))}
              />
              <span className="hr-hint">{t('hrPanel.companyDrivesOrg')}</span>
            </div>
            <div className="hr-field">
              <label className="hr-label">{t('hrPanel.titleLabel')}</label>
              <SearchableSelect
                value={form.titleRef}
                onChange={selChange('titleRef')}
                placeholder={t('hrPanel.noneOption')}
                required
                options={companyTitles.map(ti => ({ value: ti._id, label: ti.name }))}
              />
            </div>
            <div className="hr-field">
              <label className="hr-label">{t('hrPanel.department')}</label>
              <SearchableSelect
                value={form.departmentRef}
                onChange={selChange('departmentRef')}
                options={[
                  { value: '', label: t('hrPanel.noneOption') },
                  ...companyDepartments.map(d => ({ value: d._id, label: d.name })),
                ]}
              />
            </div>
            <div className="hr-field hr-field--full">
              <div className="hr-choice-options">
                <label className={`hr-choice-option ${!isManual ? 'is-active' : ''}`}>
                  <input type="radio" name="createmode" checked={!isManual} onChange={() => setMode('invite')} />
                  <span className="hr-choice-option__body">
                    <span className="hr-choice-option__title">{t('hrPanel.createModeInvite')}</span>
                    <span className="hr-choice-option__desc">{t('hrPanel.inviteEmailNotice')}</span>
                  </span>
                </label>
                <label className={`hr-choice-option ${isManual ? 'is-active' : ''}`}>
                  <input type="radio" name="createmode" checked={isManual} onChange={() => setMode('manual')} />
                  <span className="hr-choice-option__body">
                    <span className="hr-choice-option__title">{t('hrPanel.createModeManual')}</span>
                    <span className="hr-choice-option__desc">{t('hrPanel.createModeManualDesc')}</span>
                  </span>
                </label>
              </div>
            </div>
            {isManual && (
              <>
                <div className="hr-field">
                  <label className="hr-label">{t('hrPanel.passwordLabel')}</label>
                  <input className="hr-input" type="password" value={password} onChange={e => setPassword(e.target.value)} required autoComplete="new-password" />
                  <span className="hr-hint">{t('hrPanel.passwordRule')}</span>
                </div>
                <div className="hr-field">
                  <label className="hr-label">{t('hrPanel.confirmPasswordLabel')}</label>
                  <input className="hr-input" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required autoComplete="new-password" />
                </div>
                <div className="hr-field hr-field--full">
                  <label className="hr-toggle-row" htmlFor="toggle-require-pw-change">
                    <div className="hr-toggle-row__info">
                      <span className="hr-toggle-row__label">{t('hrPanel.requirePasswordChange')}</span>
                      <span className="hr-toggle-row__hint">{t('hrPanel.requirePasswordChangeHint')}</span>
                    </div>
                    <span className="hr-toggle">
                      <input id="toggle-require-pw-change" type="checkbox" checked={requireChange} onChange={e => setRequireChange(e.target.checked)} />
                      <span className="hr-toggle__track" />
                    </span>
                  </label>
                </div>
              </>
            )}
          </div>
        </form>
        <div className="hr-modal__footer">
          <button type="button" className="hr-btn hr-btn--ghost" onClick={onClose}>{t('hrPanel.cancel')}</button>
          <button type="button" className="hr-btn hr-btn--primary" disabled={saving} onClick={handleSubmit}>
            {saving
              ? (isManual ? t('hrPanel.saving') : t('hrPanel.inviting'))
              : (isManual ? t('hrPanel.createUser') : t('hrPanel.inviteUser'))}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Delete User Modal — choose soft delete (deactivate) or hard delete (permanent)
// ═══════════════════════════════════════════════════════════════════════════
function DeleteUserModal({ user, onClose, onDeactivate, onDelete }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState('deactivate'); // 'deactivate' | 'permanent'
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const isPermanent = mode === 'permanent';
  // Hard delete is irreversible — require the operator to type the email to confirm.
  const confirmed = !isPermanent || confirmText.trim().toLowerCase() === (user.email || '').toLowerCase();

  const handleConfirm = async () => {
    if (!confirmed || busy) return;
    setBusy(true); setError('');
    try {
      if (isPermanent) await onDelete(user._id);
      else await onDeactivate(user._id);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || String(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="hr-modal-overlay" onClick={onClose}>
      <div className="hr-modal hr-modal--sm" onClick={e => e.stopPropagation()} role="alertdialog" aria-modal="true">
        <div className="hr-modal__header hr-modal__header--danger">
          <div className="hr-modal__header-icon">{Icons.alert}</div>
          <div className="hr-modal__header-text">
            <h2>{t('hrPanel.deleteUser')}</h2>
            <p>{t('hrPanel.deleteUserFor', { name: `${user.firstName} ${user.lastName}` })}</p>
          </div>
          <button className="hr-modal__close" onClick={onClose} aria-label={t('hrPanel.cancel')}>{Icons.x}</button>
        </div>
        <div className="hr-modal__body">
          {error && (
            <div className="hr-alert hr-alert--error" role="alert">
              {Icons.alert}<span>{error}</span>
            </div>
          )}
          <div className="hr-delete-options">
            <label className={`hr-delete-option ${mode === 'deactivate' ? 'is-active' : ''}`}>
              <input type="radio" name="delmode" value="deactivate" checked={!isPermanent} onChange={() => setMode('deactivate')} />
              <span className="hr-delete-option__body">
                <span className="hr-delete-option__title">{t('hrPanel.softDelete')}</span>
                <span className="hr-delete-option__desc">{t('hrPanel.softDeleteDesc')}</span>
              </span>
            </label>
            <label className={`hr-delete-option hr-delete-option--danger ${isPermanent ? 'is-active' : ''}`}>
              <input type="radio" name="delmode" value="permanent" checked={isPermanent} onChange={() => setMode('permanent')} />
              <span className="hr-delete-option__body">
                <span className="hr-delete-option__title">{t('hrPanel.hardDelete')}</span>
                <span className="hr-delete-option__desc">{t('hrPanel.hardDeleteDesc')}</span>
              </span>
            </label>
          </div>
          {isPermanent && (
            <div className="hr-field hr-delete-confirm">
              <label className="hr-label">{t('hrPanel.typeEmailToConfirm')}</label>
              <input
                className="hr-input hr-input--mono"
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                placeholder={user.email}
                autoFocus
                autoComplete="off"
                spellCheck={false}
              />
            </div>
          )}
        </div>
        <div className="hr-modal__footer">
          <button className="hr-btn hr-btn--ghost" onClick={onClose}>{t('hrPanel.cancel')}</button>
          <button className="hr-btn hr-btn--danger" disabled={!confirmed || busy} onClick={handleConfirm}>
            {busy ? t('hrPanel.saving') : isPermanent ? t('hrPanel.hardDelete') : t('hrPanel.softDelete')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Users Tab
// ═══════════════════════════════════════════════════════════════════════════
function UsersTab({ users, departments, positions, titles, companies, loading, dispatch }) {
  const { t } = useTranslation();
  const currentUserId = useSelector(s => s.auth.user?._id);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [quickFilter, setQuickFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    dispatch(fetchUsers({ limit: 100 }));
  }, [dispatch]);

  const filtered = useMemo(() => users.filter(u => {
    if (search && !`${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(search.toLowerCase())) return false;
    if (roleFilter && u.role !== roleFilter) return false;
    if (companyFilter && companyIdOf(u) !== companyFilter) return false;
    if (deptFilter && (u.departmentRef?._id || u.departmentRef) !== deptFilter) return false;
    if (statusFilter !== '' && String(u.isActive) !== statusFilter) return false;
    if (quickFilter === 'active' && !u.isActive) return false;
    if (quickFilter === 'inactive' && u.isActive) return false;
    if (quickFilter === 'unassigned' && (u.departmentRef || u.positionRef)) return false;
    if (quickFilter === 'admins' && !['tenant_owner', 'tenant_admin'].includes(u.role)) return false;
    return true;
  }), [users, search, roleFilter, companyFilter, deptFilter, statusFilter, quickFilter]);

  const activeFiltersCount = [roleFilter, companyFilter, deptFilter, statusFilter].filter(Boolean).length;

  const handleSaveUser = async (id, data) => {
    const result = await dispatch(updateUser({ id, data }));
    if (result.error) throw result.payload;
    dispatch(fetchUsers({ limit: 100 }));
  };

  const handleInviteUser = async (data) => {
    const result = await dispatch(createUser(data));
    if (result.error) throw result.payload;
    dispatch(fetchUsers({ limit: 100 }));
  };

  const handleDeactivateUser = async (id) => {
    const result = await dispatch(deactivateUser(id));
    if (result.error) throw result.payload;
  };

  const handleDeleteUser = async (id) => {
    const result = await dispatch(deleteUser(id));
    if (result.error) throw result.payload;
  };

  const clearFilters = useCallback(() => {
    setRoleFilter(''); setCompanyFilter(''); setDeptFilter(''); setStatusFilter(''); setQuickFilter('all'); setSearch('');
  }, []);

  const quickChips = [
    { id: 'all', label: t('hrPanel.quickAll'), count: users.length },
    { id: 'active', label: t('hrPanel.quickActive'), count: users.filter(u => u.isActive).length },
    { id: 'inactive', label: t('hrPanel.quickInactive'), count: users.filter(u => !u.isActive).length },
    { id: 'admins', label: t('hrPanel.quickAdmins'), count: users.filter(u => ['tenant_owner', 'tenant_admin'].includes(u.role)).length },
    { id: 'unassigned', label: t('hrPanel.quickUnassigned'), count: users.filter(u => !u.departmentRef && !u.positionRef).length },
  ];

  return (
    <div className="hr__content" key="users">
      {/* Toolbar */}
      <div className="hr-toolbar">
        <div className="hr-toolbar__left">
          <div className="hr-search">
            <span className="hr-search__icon">{Icons.search}</span>
            <input
              className="hr-search__input"
              placeholder={t('hrPanel.searchByNameOrEmail')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              aria-label={t('hrPanel.searchByNameOrEmail')}
            />
            {search && (
              <button className="hr-search__clear" onClick={() => setSearch('')} aria-label={t('hrPanel.clearAll')}>
                {Icons.x}
              </button>
            )}
          </div>
        </div>
        <div className="hr-toolbar__right">
          <button className="hr-btn hr-btn--primary" onClick={() => setShowInvite(true)}>
            {Icons.plus} {t('hrPanel.inviteUser')}
          </button>
        </div>
      </div>

      {/* Quick Chips */}
      <div className="hr-chipbar" role="tablist" aria-label={t('hrPanel.quickFilters')}>
        {quickChips.map(chip => (
          <button
            key={chip.id}
            className={`hr-chipbtn ${quickFilter === chip.id ? 'is-active' : ''}`}
            onClick={() => setQuickFilter(chip.id)}
            role="tab"
            aria-selected={quickFilter === chip.id}
          >
            <span>{chip.label}</span>
            <span className="hr-chipbtn__count">{chip.count}</span>
          </button>
        ))}
      </div>

      {/* Advanced Filters */}
      <div className="hr-filter-bar">
        <div className="hr-filter-bar__label">
          {Icons.filter}
          <span>{t('hrPanel.advancedFilters')}</span>
          {activeFiltersCount > 0 && <span className="hr-filter-bar__count">{activeFiltersCount}</span>}
        </div>
        <div className="hr-filter-bar__filters">
          <div className="hr-filter-group">
            <span className="hr-filter-group__label">{t('hrPanel.role')}</span>
            <SearchableSelect
              value={roleFilter}
              onChange={setRoleFilter}
              options={[
                { value: '', label: t('hrPanel.allRoles') },
                ...Object.keys(ROLE_COLORS).map(v => ({ value: v, label: t('users.roles.' + v), color: ROLE_COLORS[v] })),
              ]}
            />
          </div>
          <div className="hr-filter-group">
            <span className="hr-filter-group__label">{t('hrPanel.company')}</span>
            <SearchableSelect
              value={companyFilter}
              onChange={setCompanyFilter}
              options={[
                { value: '', label: t('hrPanel.allCompanies') },
                ...(companies || []).map(c => ({ value: c._id, label: c.name })),
              ]}
            />
          </div>
          <div className="hr-filter-group">
            <span className="hr-filter-group__label">{t('hrPanel.department')}</span>
            <SearchableSelect
              value={deptFilter}
              onChange={setDeptFilter}
              options={[
                { value: '', label: t('hrPanel.allDepartments') },
                ...departments.map(d => ({ value: d._id, label: d.name })),
              ]}
            />
          </div>
          <div className="hr-filter-group">
            <span className="hr-filter-group__label">{t('hrPanel.status')}</span>
            <SearchableSelect
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: '', label: t('hrPanel.all') },
                { value: 'true', label: t('common.active') },
                { value: 'false', label: t('common.inactive') },
              ]}
            />
          </div>
          {activeFiltersCount > 0 && (
            <button className="hr-filter-bar__clear" onClick={() => { setRoleFilter(''); setCompanyFilter(''); setDeptFilter(''); setStatusFilter(''); }}>
              {t('hrPanel.clearAll')}
            </button>
          )}
        </div>
        <div className="hr-filter-bar__results">
          {t('hrPanel.usersOfTotal', { count: filtered.length, total: users.length })}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="hr-skeleton">
          {[...Array(5)].map((_, i) => <div key={i} className="hr-skeleton__row" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="hr-empty">
          <div className="hr-empty__icon">{Icons.users}</div>
          <p className="hr-empty__text">{t('hrPanel.noUsersFound')}</p>
          <p className="hr-empty__sub">{t('hrPanel.tryAdjustingFilters')}</p>
          <button className="hr-btn hr-btn--ghost" onClick={clearFilters}>{t('hrPanel.clearAll')}</button>
        </div>
      ) : (
        <div className="hr-table-wrap">
          <table className="hr-table">
            <thead>
              <tr>
                <th>{t('hrPanel.columns.user')}</th>
                <th>{t('hrPanel.columns.roleCol')}</th>
                <th>{t('hrPanel.columns.company')}</th>
                <th>{t('hrPanel.columns.department')}</th>
                <th>{t('hrPanel.columns.position')}</th>
                <th>{t('hrPanel.columns.manager')}</th>
                <th>{t('hrPanel.columns.status')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => {
                const roleColor = ROLE_COLORS[u.role] || '#6366f1';
                return (
                  <tr key={u._id} className="hr-table__row" onClick={() => setSelectedUser(u)} tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedUser(u); } }}>
                    <td>
                      <div className="hr-table-user">
                        <Avatar user={u} size={36} />
                        <div>
                          <div className="hr-table-user__name">{u.firstName} {u.lastName}</div>
                          <div className="hr-table-user__email">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="hr-chip hr-chip--sm" style={{ color: roleColor, background: roleColor + '15' }}>
                        {t('users.roles.' + u.role)}
                      </span>
                    </td>
                    <td>
                      {u.companyId?.name
                        ? <span className="hr-code-badge">{u.companyId.name}</span>
                        : <span className="hr-empty-chip">—</span>}
                    </td>
                    <td>
                      {u.departmentRef?.name
                        ? <span className="hr-dept-tag"><span className="hr-dept-tag__dot" style={{ background: u.departmentRef?.color || '#6366f1' }} />{u.departmentRef.name}</span>
                        : <span className="hr-empty-chip">—</span>
                      }
                    </td>
                    <td>
                      {u.positionRef
                        ? <span className="hr-code-badge">{u.positionRef.name}</span>
                        : <span className="hr-empty-chip">—</span>}
                    </td>
                    <td>{u.manager ? `${u.manager.firstName} ${u.manager.lastName}` : <span className="hr-empty-chip">—</span>}</td>
                    <td>
                      <span className={`hr-chip hr-chip--sm hr-chip--${u.isActive ? 'success' : 'muted'}`}>
                        <span className="hr-chip__dot" />
                        {u.isActive ? t('hrPanel.activeStatus') : t('hrPanel.inactiveStatus')}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selectedUser && (
        <UserDrawer
          user={selectedUser}
          users={users}
          departments={departments}
          positions={positions}
          titles={titles}
          companies={companies}
          onClose={() => setSelectedUser(null)}
          onSave={handleSaveUser}
          canDelete={selectedUser.role !== 'tenant_owner' && selectedUser._id !== currentUserId}
          onRequestDelete={() => { setDeleteTarget(selectedUser); setSelectedUser(null); }}
        />
      )}

      {showInvite && (
        <InviteUserModal
          departments={departments}
          titles={titles}
          companies={companies}
          onClose={() => setShowInvite(false)}
          onSave={handleInviteUser}
        />
      )}

      {deleteTarget && (
        <DeleteUserModal
          user={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeactivate={handleDeactivateUser}
          onDelete={handleDeleteUser}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Department Modal
// ═══════════════════════════════════════════════════════════════════════════
function DeptModal({ dept, departments, users, companies, onClose, onSave, onRequestDelete }) {
  const { t } = useTranslation();
  const isEdit = !!dept;
  const authUser = useSelector(s => s.auth.user);
  const defaultCompanyId = authUser?.companyId?._id || authUser?.companyId || '';
  const [form, setForm] = useState({
    name: dept?.name || '',
    code: dept?.code || '',
    description: dept?.description || '',
    color: dept?.color || '#6366f1',
    companyId: dept?.companyId?._id || dept?.companyId || defaultCompanyId,
    parentDepartment: dept?.parentDepartment?._id || dept?.parentDepartment || '',
    headUser: dept?.headUser?._id || dept?.headUser || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) { setError(t('hrPanel.departmentNameRequiredError')); return; }
    if (!form.companyId) { setError(t('hrPanel.companyRequiredError')); return; }
    setSaving(true); setError('');
    try { await onSave(form); onClose(); }
    catch (err) { setError(err); }
    finally { setSaving(false); }
  };

  // One org tree per company: only same-company units may be the parent.
  const parentOptions = departments.filter(d => d._id !== dept?._id && matchesCompany(d, form.companyId));

  return (
    <div className="hr-modal-overlay" onClick={onClose}>
      <div className="hr-modal hr-modal--dept" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        {/* Color-accented header with live preview */}
        <div className="hr-modal__header hr-modal__header--dept" style={{ '--dept-color': form.color }}>
          <div className="hr-modal__header-preview">
            <div className="hr-dept-preview__icon" style={{ background: form.color + '20', color: form.color }}>
              {Icons.building}
            </div>
            <div>
              <div className="hr-dept-preview__name">
                {form.name || <span className="hr-dept-preview__placeholder">{t('hrPanel.department')}</span>}
                {form.code && <span className="hr-dept-preview__code">{form.code}</span>}
              </div>
              <h2 className="hr-modal__title">{isEdit ? t('hrPanel.editDepartment') : t('hrPanel.newDepartment')}</h2>
            </div>
          </div>
          <button className="hr-modal__close" onClick={onClose} aria-label={t('hrPanel.cancel')}>{Icons.x}</button>
        </div>

        <div className="hr-modal__body">
          {error && (
            <div className="hr-alert hr-alert--error" role="alert">
              {Icons.alert}<span>{error}</span>
            </div>
          )}

          <div className="hr-section">
            <div className="hr-section__title">{Icons.info} {t('hrPanel.basicInformation')}</div>
            <div className="hr-form-grid">
              <div className="hr-field hr-field--full">
                <label className="hr-label">{t('hrPanel.companyRequired')}</label>
                <SearchableSelect
                  value={form.companyId}
                  onChange={v => setForm(f => ({ ...f, companyId: v, parentDepartment: '' }))}
                  placeholder={t('hrPanel.selectCompany')}
                  required
                  disabled={isEdit}
                  options={(companies || []).filter(c => c.isActive !== false || c._id === form.companyId).map(c => ({ value: c._id, label: c.name }))}
                />
                {isEdit && <span className="hr-field__hint">{Icons.info} {t('hrPanel.companyLockedHint')}</span>}
              </div>
              <div className="hr-field">
                <label className="hr-label">{t('hrPanel.departmentNameRequired')}</label>
                <input
                  className="hr-input"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Engineering"
                  autoFocus
                  required
                />
              </div>
              <div className="hr-field">
                <label className="hr-label">{t('hrPanel.shortCode')}</label>
                <input
                  className="hr-input hr-input--mono"
                  value={form.code}
                  onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                  placeholder="ENG"
                  maxLength={10}
                />
              </div>
              <div className="hr-field hr-field--full">
                <label className="hr-label">{t('hrPanel.description')}</label>
                <textarea
                  className="hr-textarea"
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder={t('hrPanel.descriptionPlaceholder')}
                  rows={2}
                />
              </div>
            </div>
          </div>

          <div className="hr-section">
            <div className="hr-section__title">{Icons.palette} {t('hrPanel.color')}</div>
            <div className="hr-color-picker">
              {DEPT_PRESET_COLORS.map(c => (
                <button
                  key={c}
                  type="button"
                  className={`hr-color-swatch ${form.color === c ? 'is-active' : ''}`}
                  style={{ '--swatch-color': c }}
                  onClick={() => setForm(f => ({ ...f, color: c }))}
                  aria-label={c}
                  title={c}
                />
              ))}
              <label className="hr-color-custom" title="Custom color">
                <input
                  type="color"
                  value={form.color}
                  onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
                />
                <span className="hr-color-custom__label">{form.color}</span>
              </label>
            </div>
          </div>

          <div className="hr-section">
            <div className="hr-section__title">{Icons.building} {t('hrPanel.structure')}</div>
            <div className="hr-form-grid">
              <div className="hr-field">
                <label className="hr-label">{t('hrPanel.parentDepartment')}</label>
                <SearchableSelect
                  value={form.parentDepartment}
                  onChange={v => setForm(f => ({ ...f, parentDepartment: v }))}
                  options={[
                    { value: '', label: t('hrPanel.topLevel') },
                    ...parentOptions.map(d => ({ value: d._id, label: d.name })),
                  ]}
                />
              </div>
              <div className="hr-field">
                <label className="hr-label">{t('hrPanel.departmentHead')}</label>
                <SearchableSelect
                  value={form.headUser}
                  onChange={v => setForm(f => ({ ...f, headUser: v }))}
                  options={[
                    { value: '', label: t('hrPanel.noneOption') },
                    ...users.filter(u => u.isActive).map(u => ({ value: u._id, label: `${u.firstName} ${u.lastName}` })),
                  ]}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="hr-modal__footer">
          {isEdit && (
            <button type="button" className="hr-btn hr-btn--danger-ghost hr-modal__footer-lead" onClick={onRequestDelete}>
              {Icons.trash} {t('hrPanel.deleteDepartment')}
            </button>
          )}
          <button type="button" className="hr-btn hr-btn--ghost" onClick={onClose}>{t('hrPanel.cancel')}</button>
          <button type="button" className="hr-btn hr-btn--primary" disabled={saving} onClick={handleSubmit}>
            {saving ? t('hrPanel.saving') : isEdit ? t('hrPanel.saveChanges') : t('hrPanel.createDepartment')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Departments Tab
// ═══════════════════════════════════════════════════════════════════════════
function DepartmentsTab({ departments, users, companies, loading, dispatch }) {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [editDept, setEditDept] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');

  useEffect(() => { dispatch(fetchDepartments()); }, [dispatch]);

  const handleSave = async (data) => {
    if (editDept) {
      const r = await dispatch(updateDepartment({ id: editDept._id, data }));
      if (r.error) throw r.payload;
    } else {
      const r = await dispatch(createDepartment(data));
      if (r.error) throw r.payload;
    }
  };

  const handleDelete = async (dept) => {
    const r = await dispatch(deleteDepartment(dept._id));
    if (r.error) { setError(r.payload); }
    setDeleteConfirm(null);
  };

  const visibleDepartments = companyFilter ? departments.filter(d => companyIdOf(d) === companyFilter) : departments;
  const topLevel = visibleDepartments.filter(d => !d.parentDepartment);
  const children = (parentId) => visibleDepartments.filter(d => (d.parentDepartment?._id || d.parentDepartment) === parentId);
  const matchesSearch = (dept) => !search || dept.name.toLowerCase().includes(search.toLowerCase());
  const companyNameOf = (dept) => dept.companyId?.name
    || (companies || []).find(c => c._id === companyIdOf(dept))?.name || '';

  const DeptCard = ({ dept, level = 0 }) => {
    const subDepts = children(dept._id);
    if (!matchesSearch(dept) && !subDepts.some(matchesSearch)) return null;
    const deptColor = dept.color || '#6366f1';
    return (
      <div className={`hr-dept-wrap ${level > 0 ? 'is-child' : ''}`} style={{ '--indent': `${level * 28}px` }}>
        {level > 0 && <span className="hr-dept-tree-line" aria-hidden="true" />}
        <div className="hr-dept-card hr-dept-card--clickable" style={{ '--dept-c': deptColor }} tabIndex={0} role="button"
             onClick={() => { setEditDept(dept); setShowModal(true); }}
             onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditDept(dept); setShowModal(true); } }}>
          <div className="hr-dept-card__accent" />
          <div className="hr-dept-card__body">
            <div className="hr-dept-card__top">
              <div className="hr-dept-card__identity">
                <div className="hr-dept-card__icon">{Icons.building}</div>
                <div className="hr-dept-card__names">
                  <div className="hr-dept-card__name">{dept.name}</div>
                  {dept.code && <span className="hr-dept-card__code">{Icons.hash} {dept.code}</span>}
                </div>
              </div>
            </div>
            {dept.description && <p className="hr-dept-card__desc">{dept.description}</p>}
            <div className="hr-dept-card__meta">
              {companyNameOf(dept) && (
                <span className="hr-dept-card__meta-item">{Icons.company} {companyNameOf(dept)}</span>
              )}
              {dept.headUser && (
                <span className="hr-dept-card__meta-item">
                  <Avatar user={dept.headUser} size={20} />
                  {dept.headUser.firstName} {dept.headUser.lastName}
                </span>
              )}
              <span className="hr-dept-card__meta-item">{Icons.team} {dept.memberCount || 0} {t('hrPanel.members')}</span>
              {subDepts.length > 0 && (
                <span className="hr-dept-card__meta-item">
                  {Icons.building} {subDepts.length} {subDepts.length !== 1 ? t('hrPanel.subDeptsPlural') : t('hrPanel.subDepts')}
                </span>
              )}
            </div>
          </div>
        </div>
        {subDepts.length > 0 && (
          <div className="hr-dept-children">
            {subDepts.map(child => <DeptCard key={child._id} dept={child} level={level + 1} />)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="hr__content" key="departments">
      <div className="hr-toolbar">
        <div className="hr-toolbar__left">
          <div className="hr-search">
            <span className="hr-search__icon">{Icons.search}</span>
            <input
              className="hr-search__input"
              placeholder={t('hrPanel.searchDepartments')}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && <button className="hr-search__clear" onClick={() => setSearch('')} aria-label={t('hrPanel.clearAll')}>{Icons.x}</button>}
          </div>
          <div className="hr-toolbar__select">
            <SearchableSelect
              value={companyFilter}
              onChange={setCompanyFilter}
              options={[
                { value: '', label: t('hrPanel.allCompanies') },
                ...(companies || []).map(c => ({ value: c._id, label: c.name })),
              ]}
            />
          </div>
        </div>
        <div className="hr-toolbar__right">
          <button className="hr-btn hr-btn--primary" onClick={() => { setEditDept(null); setShowModal(true); }}>
            {Icons.plus} {t('hrPanel.newDepartment')}
          </button>
        </div>
      </div>

      {error && (
        <div className="hr-alert hr-alert--error" role="alert">
          {Icons.alert}<span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="hr-skeleton">
          {[...Array(3)].map((_, i) => <div key={i} className="hr-skeleton__card" />)}
        </div>
      ) : topLevel.length === 0 ? (
        <div className="hr-empty">
          <div className="hr-empty__icon">{Icons.building}</div>
          <p className="hr-empty__text">{t('hrPanel.noDepartmentsYet')}</p>
          <p className="hr-empty__sub">{t('hrPanel.createFirstDepartment')}</p>
          <button className="hr-btn hr-btn--primary" onClick={() => { setEditDept(null); setShowModal(true); }}>
            {Icons.plus} {t('hrPanel.newDepartment')}
          </button>
        </div>
      ) : (
        <div className="hr-dept-list">
          {topLevel.map(d => <DeptCard key={d._id} dept={d} />)}
        </div>
      )}

      {showModal && (
        <DeptModal
          dept={editDept}
          departments={departments}
          users={users}
          companies={companies}
          onClose={() => { setShowModal(false); setEditDept(null); }}
          onSave={handleSave}
          onRequestDelete={() => { setShowModal(false); setDeleteConfirm(editDept); }}
        />
      )}

      {deleteConfirm && (
        <div className="hr-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="hr-modal hr-modal--sm" onClick={e => e.stopPropagation()} role="alertdialog" aria-modal="true">
            <div className="hr-modal__header hr-modal__header--danger">
              <div className="hr-modal__header-icon">{Icons.alert}</div>
              <div className="hr-modal__header-text">
                <h2>{t('hrPanel.deleteDepartment')}</h2>
                <p>{t('hrPanel.deleteDepartmentConfirm', { name: deleteConfirm.name })}</p>
              </div>
              <button className="hr-modal__close" onClick={() => setDeleteConfirm(null)} aria-label={t('hrPanel.cancel')}>{Icons.x}</button>
            </div>
            <div className="hr-modal__body">
              <p className="hr-confirm__sub">{t('hrPanel.deleteDepartmentSub')}</p>
            </div>
            <div className="hr-modal__footer">
              <button className="hr-btn hr-btn--ghost" onClick={() => setDeleteConfirm(null)}>{t('hrPanel.cancel')}</button>
              <button className="hr-btn hr-btn--danger" onClick={() => handleDelete(deleteConfirm)}>{t('hrPanel.deleteDepartment')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Position Modal
// ═══════════════════════════════════════════════════════════════════════════
function PositionModal({ position, users, companies, onClose, onSave }) {
  const { t } = useTranslation();
  const authUser = useSelector(s => s.auth.user);
  const defaultCompanyId = authUser?.companyId?._id || authUser?.companyId || '';
  const [form, setForm] = useState({
    name: position?.name || '',
    code: position?.code || '',
    description: position?.description || '',
    companyId: position?.companyId?._id || position?.companyId || defaultCompanyId,
    assignedUser: position?.assignedUser?._id || position?.assignedUser || '',
    isActive: position ? position.isActive !== false : true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    // The holder must belong to the position's company.
    if (name === 'companyId') {
      setForm(f => ({ ...f, companyId: value, assignedUser: '' }));
      return;
    }
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.companyId) { setError(t('hrPanel.companyRequiredError')); return; }
    setSaving(true); setError('');
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const companyUsers = users.filter(u => u.isActive && matchesCompany(u, form.companyId));

  return (
    <div className="hr-modal-overlay" onClick={onClose}>
      <div className="hr-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="hr-modal__header hr-modal__header--accent">
          <div className="hr-modal__header-icon">{Icons.briefcase}</div>
          <div className="hr-modal__header-text">
            <h2>{position ? t('hrPanel.editPosition') : t('hrPanel.newPosition')}</h2>
            <p>{t('hrPanel.positionSingleUser')}</p>
          </div>
          <button className="hr-modal__close" onClick={onClose} aria-label={t('hrPanel.cancel')}>{Icons.x}</button>
        </div>
        <form onSubmit={handleSubmit} className="hr-modal__body">
          {error && (
            <div className="hr-alert hr-alert--error" role="alert">
              {Icons.alert}<span>{error}</span>
            </div>
          )}
          <div className="hr-form-grid">
            <div className="hr-field hr-field--full">
              <label className="hr-label">{t('hrPanel.companyRequired')}</label>
              <SearchableSelect
                value={form.companyId}
                onChange={v => handleChange({ target: { name: 'companyId', value: v } })}
                placeholder={t('hrPanel.selectCompany')}
                required
                disabled={!!position}
                options={(companies || []).filter(c => c.isActive !== false || c._id === form.companyId).map(c => ({ value: c._id, label: c.name }))}
              />
              {!!position && <span className="hr-field__hint">{Icons.info} {t('hrPanel.companyLockedHint')}</span>}
            </div>
            <div className="hr-field">
              <label className="hr-label">{t('hrPanel.positionNameRequired')}</label>
              <input className="hr-input" name="name" value={form.name} onChange={handleChange} required placeholder="e.g. Chief Legal Officer" autoFocus />
            </div>
            <div className="hr-field">
              <label className="hr-label">{t('hrPanel.codeRequired')}</label>
              <input className="hr-input hr-input--mono" name="code" value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} required placeholder="CLO-001" />
              <span className="hr-field__hint">{Icons.info} {t('hrPanel.codeHint')}</span>
            </div>
            <div className="hr-field hr-field--full">
              <label className="hr-label">{t('hrPanel.description')}</label>
              <input className="hr-input" name="description" value={form.description} onChange={handleChange} placeholder={t('hrPanel.optionalDescription')} />
            </div>
            <div className="hr-field hr-field--full">
              <label className="hr-label">{t('hrPanel.assignedUser')}</label>
              <SearchableSelect
                value={form.assignedUser}
                onChange={v => handleChange({ target: { name: 'assignedUser', value: v } })}
                options={[
                  { value: '', label: t('hrPanel.unassigned') },
                  ...companyUsers.map(u => ({ value: u._id, label: `${u.firstName} ${u.lastName}`, sublabel: u.email })),
                ]}
              />
              <span className="hr-field__hint">{Icons.info} {t('hrPanel.positionSingleUser')}</span>
            </div>
            <div className="hr-field hr-field--full">
              <label className="hr-toggle-row" htmlFor="position-active">
                <div className="hr-toggle-row__info">
                  <span className="hr-toggle-row__label">{t('hrPanel.activeRecord')}</span>
                  <span className="hr-toggle-row__hint">{t('hrPanel.activeRecordHint')}</span>
                </div>
                <span className="hr-toggle">
                  <input id="position-active" type="checkbox" name="isActive" checked={form.isActive} onChange={handleChange} />
                  <span className="hr-toggle__track" />
                </span>
              </label>
            </div>
          </div>
        </form>
        <div className="hr-modal__footer">
          <button type="button" className="hr-btn hr-btn--ghost" onClick={onClose}>{t('hrPanel.cancel')}</button>
          <button type="button" className="hr-btn hr-btn--primary" disabled={saving} onClick={handleSubmit}>
            {saving ? t('hrPanel.saving') : position ? t('hrPanel.saveChanges') : t('hrPanel.createPosition')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Positions Tab
// ═══════════════════════════════════════════════════════════════════════════
function PositionsTab({ users, companies }) {
  const { t } = useTranslation();
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editPosition, setEditPosition] = useState(null);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await positionsAPI.list();
      setPositions(res.data.positions || []);
    } catch (err) {
      setError(t('hrPanel.failedToLoadPositions'));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() =>
    positions.filter(p =>
      (!companyFilter || companyIdOf(p) === companyFilter) &&
      (!search ||
        p.name.toLowerCase().includes(search.toLowerCase()) ||
        p.code.toLowerCase().includes(search.toLowerCase()))
    ), [positions, search, companyFilter]);

  const handleSave = async (data) => {
    if (editPosition) {
      await positionsAPI.update(editPosition._id, data);
    } else {
      await positionsAPI.create(data);
    }
    await load();
  };

  return (
    <div className="hr__content" key="positions">
      <div className="hr-toolbar">
        <div className="hr-toolbar__left">
          <div className="hr-search">
            <span className="hr-search__icon">{Icons.search}</span>
            <input
              className="hr-search__input"
              placeholder={t('hrPanel.searchPositions')}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && <button className="hr-search__clear" onClick={() => setSearch('')} aria-label={t('hrPanel.clearAll')}>{Icons.x}</button>}
          </div>
          <div className="hr-toolbar__select">
            <SearchableSelect
              value={companyFilter}
              onChange={setCompanyFilter}
              options={[
                { value: '', label: t('hrPanel.allCompanies') },
                ...(companies || []).map(c => ({ value: c._id, label: c.name })),
              ]}
            />
          </div>
        </div>
        <div className="hr-toolbar__right">
          <button className="hr-btn hr-btn--primary" onClick={() => { setEditPosition(null); setShowModal(true); }}>
            {Icons.plus} {t('hrPanel.newPosition')}
          </button>
        </div>
      </div>

      {error && (
        <div className="hr-alert hr-alert--error" role="alert">
          {Icons.alert}<span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="hr-skeleton">
          {[...Array(4)].map((_, i) => <div key={i} className="hr-skeleton__row" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="hr-empty">
          <div className="hr-empty__icon">{Icons.briefcase}</div>
          <p className="hr-empty__text">{t('hrPanel.noPositionsYet')}</p>
          <p className="hr-empty__sub">{t('hrPanel.createPositionsSub')}</p>
          <button className="hr-btn hr-btn--primary" onClick={() => { setEditPosition(null); setShowModal(true); }}>
            {Icons.plus} {t('hrPanel.newPosition')}
          </button>
        </div>
      ) : (
        <div className="hr-table-wrap">
          <table className="hr-table">
            <thead>
              <tr>
                <th>{t('hrPanel.columns.name')}</th>
                <th>{t('hrPanel.columns.code')}</th>
                <th>{t('hrPanel.columns.company')}</th>
                <th>{t('hrPanel.assignedUser')}</th>
                <th>{t('hrPanel.description')}</th>
                <th>{t('hrPanel.columns.status')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(pos => (
                <tr key={pos._id} className="hr-table__row" tabIndex={0}
                    onClick={() => { setEditPosition(pos); setShowModal(true); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditPosition(pos); setShowModal(true); } }}>
                  <td>
                    <div className="hr-table-primary">
                      <div className="hr-dot-icon" style={{ background: $primaryTint(pos.code) }}>{Icons.briefcase}</div>
                      <strong>{pos.name}</strong>
                    </div>
                  </td>
                  <td><span className="hr-code-badge">{pos.code}</span></td>
                  <td>{pos.companyId?.name ? <span className="hr-code-badge">{pos.companyId.name}</span> : <span className="hr-empty-chip">—</span>}</td>
                  <td>
                    {pos.assignedUser
                      ? (
                        <div className="hr-assigned-user">
                          <Avatar user={pos.assignedUser} size={24} />
                          <span>{pos.assignedUser.firstName} {pos.assignedUser.lastName}</span>
                        </div>
                      )
                      : <span className="hr-empty-chip">{t('hrPanel.unassigned')}</span>
                    }
                  </td>
                  <td className="hr-table__desc">{pos.description || '—'}</td>
                  <td>
                    <span className={`hr-chip hr-chip--sm hr-chip--${pos.isActive ? 'success' : 'muted'}`}>
                      <span className="hr-chip__dot" />
                      {pos.isActive ? t('hrPanel.activeStatus') : t('hrPanel.inactiveStatus')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <PositionModal
          position={editPosition}
          users={users}
          companies={companies}
          onClose={() => { setShowModal(false); setEditPosition(null); }}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

// Helper: stable deterministic tint from a code
function $primaryTint(code = '') {
  const palette = ['#6366f115', '#0891b215', '#05966915', '#d9770615', '#7c3aed15', '#db277715', '#0284c715'];
  let h = 0;
  for (let i = 0; i < code.length; i++) h = (h * 31 + code.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
}

// ═══════════════════════════════════════════════════════════════════════════
// Title Modal
// ═══════════════════════════════════════════════════════════════════════════
function TitleModal({ title, companies, onClose, onSave }) {
  const { t } = useTranslation();
  const authUser = useSelector(s => s.auth.user);
  const defaultCompanyId = authUser?.companyId?._id || authUser?.companyId || '';
  const [form, setForm] = useState({
    name: title?.name || '',
    code: title?.code || '',
    description: title?.description || '',
    companyId: title?.companyId?._id || title?.companyId || defaultCompanyId,
    isActive: title ? title.isActive !== false : true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => setForm(f => ({
    ...f, [e.target.name]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.companyId) { setError(t('hrPanel.companyRequiredError')); return; }
    setSaving(true); setError('');
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="hr-modal-overlay" onClick={onClose}>
      <div className="hr-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="hr-modal__header hr-modal__header--accent">
          <div className="hr-modal__header-icon">{Icons.badge}</div>
          <div className="hr-modal__header-text">
            <h2>{title ? t('hrPanel.editTitle') : t('hrPanel.newTitle')}</h2>
            <p>{t('hrPanel.titlesHint')}</p>
          </div>
          <button className="hr-modal__close" onClick={onClose} aria-label={t('hrPanel.cancel')}>{Icons.x}</button>
        </div>
        <form onSubmit={handleSubmit} className="hr-modal__body">
          {error && (
            <div className="hr-alert hr-alert--error" role="alert">
              {Icons.alert}<span>{error}</span>
            </div>
          )}
          <div className="hr-form-grid">
            <div className="hr-field hr-field--full">
              <label className="hr-label">{t('hrPanel.companyRequired')}</label>
              <SearchableSelect
                value={form.companyId}
                onChange={v => handleChange({ target: { name: 'companyId', value: v } })}
                placeholder={t('hrPanel.selectCompany')}
                required
                disabled={!!title}
                options={(companies || []).filter(c => c.isActive !== false || c._id === form.companyId).map(c => ({ value: c._id, label: c.name }))}
              />
              {!!title && <span className="hr-field__hint">{Icons.info} {t('hrPanel.companyLockedHint')}</span>}
            </div>
            <div className="hr-field">
              <label className="hr-label">{t('hrPanel.titleNameRequired')}</label>
              <input className="hr-input" name="name" value={form.name} onChange={handleChange} required placeholder="e.g. Senior Counsel" autoFocus />
            </div>
            <div className="hr-field">
              <label className="hr-label">{t('hrPanel.codeRequired')}</label>
              <input className="hr-input hr-input--mono" name="code" value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} required placeholder="SC-001" />
              <span className="hr-field__hint">{Icons.info} {t('hrPanel.codeHint')}</span>
            </div>
            <div className="hr-field hr-field--full">
              <label className="hr-label">{t('hrPanel.description')}</label>
              <input className="hr-input" name="description" value={form.description} onChange={handleChange} placeholder={t('hrPanel.optionalDescription')} />
            </div>
            <div className="hr-field hr-field--full">
              <label className="hr-toggle-row" htmlFor="title-active">
                <div className="hr-toggle-row__info">
                  <span className="hr-toggle-row__label">{t('hrPanel.activeRecord')}</span>
                  <span className="hr-toggle-row__hint">{t('hrPanel.activeRecordHint')}</span>
                </div>
                <span className="hr-toggle">
                  <input id="title-active" type="checkbox" name="isActive" checked={form.isActive} onChange={handleChange} />
                  <span className="hr-toggle__track" />
                </span>
              </label>
            </div>
          </div>
        </form>
        <div className="hr-modal__footer">
          <button type="button" className="hr-btn hr-btn--ghost" onClick={onClose}>{t('hrPanel.cancel')}</button>
          <button type="button" className="hr-btn hr-btn--primary" disabled={saving} onClick={handleSubmit}>
            {saving ? t('hrPanel.saving') : title ? t('hrPanel.saveChanges') : t('hrPanel.createTitle')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Titles Tab
// ═══════════════════════════════════════════════════════════════════════════
function TitlesTab({ companies }) {
  const { t } = useTranslation();
  const [titles, setTitles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editTitle, setEditTitle] = useState(null);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await titlesAPI.list();
      setTitles(res.data.titles || []);
    } catch (err) {
      setError(t('hrPanel.failedToLoadTitles'));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() =>
    titles.filter(ti =>
      (!companyFilter || companyIdOf(ti) === companyFilter) &&
      (!search ||
        ti.name.toLowerCase().includes(search.toLowerCase()) ||
        ti.code.toLowerCase().includes(search.toLowerCase()))
    ), [titles, search, companyFilter]);

  const handleSave = async (data) => {
    if (editTitle) {
      await titlesAPI.update(editTitle._id, data);
    } else {
      await titlesAPI.create(data);
    }
    await load();
  };

  return (
    <div className="hr__content" key="titles">
      <div className="hr-toolbar">
        <div className="hr-toolbar__left">
          <div className="hr-search">
            <span className="hr-search__icon">{Icons.search}</span>
            <input
              className="hr-search__input"
              placeholder={t('hrPanel.searchTitles')}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && <button className="hr-search__clear" onClick={() => setSearch('')} aria-label={t('hrPanel.clearAll')}>{Icons.x}</button>}
          </div>
          <div className="hr-toolbar__select">
            <SearchableSelect
              value={companyFilter}
              onChange={setCompanyFilter}
              options={[
                { value: '', label: t('hrPanel.allCompanies') },
                ...(companies || []).map(c => ({ value: c._id, label: c.name })),
              ]}
            />
          </div>
        </div>
        <div className="hr-toolbar__right">
          <button className="hr-btn hr-btn--primary" onClick={() => { setEditTitle(null); setShowModal(true); }}>
            {Icons.plus} {t('hrPanel.newTitle')}
          </button>
        </div>
      </div>

      {error && (
        <div className="hr-alert hr-alert--error" role="alert">
          {Icons.alert}<span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="hr-skeleton">
          {[...Array(4)].map((_, i) => <div key={i} className="hr-skeleton__row" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="hr-empty">
          <div className="hr-empty__icon">{Icons.badge}</div>
          <p className="hr-empty__text">{t('hrPanel.noTitlesYet')}</p>
          <p className="hr-empty__sub">{t('hrPanel.createTitlesSub')}</p>
          <button className="hr-btn hr-btn--primary" onClick={() => { setEditTitle(null); setShowModal(true); }}>
            {Icons.plus} {t('hrPanel.newTitle')}
          </button>
        </div>
      ) : (
        <div className="hr-table-wrap">
          <table className="hr-table">
            <thead>
              <tr>
                <th>{t('hrPanel.columns.name')}</th>
                <th>{t('hrPanel.columns.code')}</th>
                <th>{t('hrPanel.columns.company')}</th>
                <th>{t('hrPanel.columns.users')}</th>
                <th>{t('hrPanel.description')}</th>
                <th>{t('hrPanel.columns.status')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(ti => (
                <tr key={ti._id} className="hr-table__row" tabIndex={0}
                    onClick={() => { setEditTitle(ti); setShowModal(true); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditTitle(ti); setShowModal(true); } }}>
                  <td>
                    <div className="hr-table-primary">
                      <div className="hr-dot-icon" style={{ background: $primaryTint(ti.code) }}>{Icons.badge}</div>
                      <strong>{ti.name}</strong>
                    </div>
                  </td>
                  <td><span className="hr-code-badge">{ti.code}</span></td>
                  <td>{ti.companyId?.name ? <span className="hr-code-badge">{ti.companyId.name}</span> : <span className="hr-empty-chip">—</span>}</td>
                  <td>
                    <span className="hr-count-badge">
                      {Icons.team}
                      {ti.userCount ?? 0} {(ti.userCount !== 1) ? t('hrPanel.usersPlural') : t('hrPanel.userSingular')}
                    </span>
                  </td>
                  <td className="hr-table__desc">{ti.description || '—'}</td>
                  <td>
                    <span className={`hr-chip hr-chip--sm hr-chip--${ti.isActive ? 'success' : 'muted'}`}>
                      <span className="hr-chip__dot" />
                      {ti.isActive ? t('hrPanel.activeStatus') : t('hrPanel.inactiveStatus')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <TitleModal
          title={editTitle}
          companies={companies}
          onClose={() => { setShowModal(false); setEditTitle(null); }}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Company Modal
// ═══════════════════════════════════════════════════════════════════════════
function CompanyModal({ company, onClose, onSave }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: company?.name || '',
    code: company?.code || '',
    description: company?.description || '',
    isActive: company ? company.isActive !== false : true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => setForm(f => ({
    ...f, [e.target.name]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError(t('hrPanel.companyNameRequiredError')); return; }
    setSaving(true); setError('');
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || String(err));
    } finally { setSaving(false); }
  };

  return (
    <div className="hr-modal-overlay" onClick={onClose}>
      <div className="hr-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="hr-modal__header hr-modal__header--accent">
          <div className="hr-modal__header-icon">{Icons.company}</div>
          <div className="hr-modal__header-text">
            <h2>{company ? t('hrPanel.editCompany') : t('hrPanel.newCompany')}</h2>
            <p>{t('hrPanel.companiesHint')}</p>
          </div>
          <button className="hr-modal__close" onClick={onClose} aria-label={t('hrPanel.cancel')}>{Icons.x}</button>
        </div>
        <form onSubmit={handleSubmit} className="hr-modal__body">
          {error && (
            <div className="hr-alert hr-alert--error" role="alert">
              {Icons.alert}<span>{error}</span>
            </div>
          )}
          <div className="hr-form-grid">
            <div className="hr-field">
              <label className="hr-label">{t('hrPanel.companyNameRequired')}</label>
              <input className="hr-input" name="name" value={form.name} onChange={handleChange} required placeholder="e.g. Acme Holding A.Ş." autoFocus />
            </div>
            <div className="hr-field">
              <label className="hr-label">{t('hrPanel.shortCode')}</label>
              <input className="hr-input hr-input--mono" name="code" value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="ACME" maxLength={20} />
            </div>
            <div className="hr-field hr-field--full">
              <label className="hr-label">{t('hrPanel.description')}</label>
              <input className="hr-input" name="description" value={form.description} onChange={handleChange} placeholder={t('hrPanel.optionalDescription')} />
            </div>
            <div className="hr-field hr-field--full">
              <label className="hr-toggle-row" htmlFor="company-active">
                <div className="hr-toggle-row__info">
                  <span className="hr-toggle-row__label">{t('hrPanel.activeRecord')}</span>
                  <span className="hr-toggle-row__hint">{t('hrPanel.activeRecordHint')}</span>
                </div>
                <span className="hr-toggle">
                  <input id="company-active" type="checkbox" name="isActive" checked={form.isActive} onChange={handleChange} />
                  <span className="hr-toggle__track" />
                </span>
              </label>
            </div>
          </div>
        </form>
        <div className="hr-modal__footer">
          <button type="button" className="hr-btn hr-btn--ghost" onClick={onClose}>{t('hrPanel.cancel')}</button>
          <button type="button" className="hr-btn hr-btn--primary" disabled={saving} onClick={handleSubmit}>
            {saving ? t('hrPanel.saving') : company ? t('hrPanel.saveChanges') : t('hrPanel.createCompany')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Companies Tab — central Company master (HR owns it; contracts and document
// management consume the same definitions)
// ═══════════════════════════════════════════════════════════════════════════
function CompaniesTab({ companies, loading, dispatch }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editCompany, setEditCompany] = useState(null);

  const filtered = useMemo(() =>
    (companies || []).filter(c =>
      !search ||
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      (c.code || '').toLowerCase().includes(search.toLowerCase())
    ), [companies, search]);

  const handleSave = async (data) => {
    const action = editCompany
      ? await dispatch(updateCompany({ id: editCompany._id, data }))
      : await dispatch(createCompany(data));
    if (action.error) throw new Error(action.payload);
    dispatch(fetchCompanies());
  };

  return (
    <div className="hr__content" key="companies">
      <div className="hr-toolbar">
        <div className="hr-toolbar__left">
          <div className="hr-search">
            <span className="hr-search__icon">{Icons.search}</span>
            <input
              className="hr-search__input"
              placeholder={t('hrPanel.searchCompanies')}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && <button className="hr-search__clear" onClick={() => setSearch('')} aria-label={t('hrPanel.clearAll')}>{Icons.x}</button>}
          </div>
        </div>
        <div className="hr-toolbar__right">
          <button className="hr-btn hr-btn--primary" onClick={() => { setEditCompany(null); setShowModal(true); }}>
            {Icons.plus} {t('hrPanel.newCompany')}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="hr-skeleton">
          {[...Array(3)].map((_, i) => <div key={i} className="hr-skeleton__row" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="hr-empty">
          <div className="hr-empty__icon">{Icons.company}</div>
          <p className="hr-empty__text">{t('hrPanel.noCompaniesYet')}</p>
          <p className="hr-empty__sub">{t('hrPanel.createCompaniesSub')}</p>
          <button className="hr-btn hr-btn--primary" onClick={() => { setEditCompany(null); setShowModal(true); }}>
            {Icons.plus} {t('hrPanel.newCompany')}
          </button>
        </div>
      ) : (
        <div className="hr-table-wrap">
          <table className="hr-table">
            <thead>
              <tr>
                <th>{t('hrPanel.columns.name')}</th>
                <th>{t('hrPanel.columns.code')}</th>
                <th>{t('hrPanel.columns.users')}</th>
                <th>{t('hrPanel.description')}</th>
                <th>{t('hrPanel.columns.status')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c._id} className="hr-table__row" tabIndex={0}
                    onClick={() => { setEditCompany(c); setShowModal(true); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditCompany(c); setShowModal(true); } }}>
                  <td>
                    <div className="hr-table-primary">
                      <div className="hr-dot-icon" style={{ background: $primaryTint(c.code || c.name) }}>{Icons.company}</div>
                      <strong>{c.name}</strong>
                    </div>
                  </td>
                  <td>{c.code ? <span className="hr-code-badge">{c.code}</span> : <span className="hr-empty-chip">—</span>}</td>
                  <td>
                    <span className="hr-count-badge">
                      {Icons.team}
                      {c.memberCount ?? 0} {(c.memberCount !== 1) ? t('hrPanel.usersPlural') : t('hrPanel.userSingular')}
                    </span>
                  </td>
                  <td className="hr-table__desc">{c.description || '—'}</td>
                  <td>
                    <span className={`hr-chip hr-chip--sm hr-chip--${c.isActive !== false ? 'success' : 'muted'}`}>
                      <span className="hr-chip__dot" />
                      {c.isActive !== false ? t('hrPanel.activeStatus') : t('hrPanel.inactiveStatus')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <CompanyModal
          company={editCompany}
          onClose={() => { setShowModal(false); setEditCompany(null); }}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Group Modal — create / edit a user group (member containers: name + desc)
// ═══════════════════════════════════════════════════════════════════════════
const GROUP_COLOR = '#6366f1';


function GroupModal({ group, onClose, onSave }) {
  const { t } = useTranslation();
  const isEdit = !!group;
  const [form, setForm] = useState({ name: group?.name || '', description: group?.description || '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) { setError(t('userGroups.messages.groupNameRequired')); return; }
    setSaving(true); setError('');
    try {
      await onSave({ name: form.name.trim(), description: form.description.trim() });
      onClose();
    } catch (err) {
      setError(typeof err === 'string' ? err : t('userGroups.messages.failedToSave'));
    } finally { setSaving(false); }
  };

  return (
    <div className="hr-modal-overlay" onClick={onClose}>
      <div className="hr-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="hr-modal__header hr-modal__header--accent">
          <div className="hr-modal__header-icon">{Icons.team}</div>
          <div className="hr-modal__header-text">
            <h2>{isEdit ? t('userGroups.editGroupTitle') : t('userGroups.createGroupTitle')}</h2>
            <p>{isEdit ? group.name : t('userGroups.setupNewGroup')}</p>
          </div>
          <button className="hr-modal__close" onClick={onClose} aria-label={t('hrPanel.cancel')}>{Icons.x}</button>
        </div>
        <form onSubmit={handleSubmit} className="hr-modal__body">
          {error && (
            <div className="hr-alert hr-alert--error" role="alert">
              {Icons.alert}<span>{error}</span>
            </div>
          )}
          <div className="hr-form-grid">
            <div className="hr-field hr-field--full">
              <label className="hr-label">{t('userGroups.groupNameRequired')}</label>
              <input
                className="hr-input"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder={t('userGroups.groupNamePlaceholder')}
                autoFocus
                required
              />
            </div>
            <div className="hr-field hr-field--full">
              <label className="hr-label">{t('userGroups.description')}</label>
              <textarea
                className="hr-textarea"
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder={t('userGroups.descriptionPlaceholder')}
                rows={2}
              />
              <span className="hr-hint">{t('userGroups.permissionsManagedInRoles')}</span>
            </div>
          </div>
        </form>
        <div className="hr-modal__footer">
          <button type="button" className="hr-btn hr-btn--ghost" onClick={onClose}>{t('hrPanel.cancel')}</button>
          <button type="button" className="hr-btn hr-btn--primary" disabled={saving} onClick={handleSubmit}>
            {saving ? t('hrPanel.saving') : isEdit ? t('hrPanel.saveChanges') : t('userGroups.createGroup')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Group Drawer — group identity + member management (add / remove)
// ═══════════════════════════════════════════════════════════════════════════
function GroupDrawer({ group, allUsers, onClose, onEdit, onRequestDelete, onAddMember, onRemoveMember }) {
  const { t } = useTranslation();
  const [memberSearch, setMemberSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const members = group.members || [];
  const activeCount = members.filter(m => m.isActive !== false).length;
  const memberIds = new Set(members.map(m => m._id || m));
  const name = groupName(group, t);
  const desc = groupDesc(group, t);
  // All Employees mirrors the active user list — the system maintains it, so
  // the drawer shows members read-only. Other system groups manage members
  // normally; only rename/delete is off the table for them.
  const autoManaged = group.systemKey === 'all-employees';

  const q = userSearch.toLowerCase();
  const addable = allUsers.filter(u =>
    u.isActive && !memberIds.has(u._id) &&
    (!q || `${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(q))
  ).slice(0, 20);

  const mq = memberSearch.toLowerCase();
  const visibleMembers = mq
    ? members.filter(m => `${m.firstName || ''} ${m.lastName || ''} ${m.email || ''}`.toLowerCase().includes(mq))
    : members;

  return (
    <>
      <div className="hr-drawer-overlay" onClick={onClose} />
      <div className="hr-drawer" role="dialog" aria-modal="true" aria-label={name}>
        {/* Flat, light header — group identity + actions, no gradient cover */}
        <div className="hr-gdrawer-head">
          <div className="hr-gdrawer-head__avatar">
            {(name?.[0] || '?').toUpperCase()}
          </div>
          <div className="hr-gdrawer-head__meta">
            <h3 className="hr-gdrawer-head__name">
              {name}
              {group.isSystem && <span className="hr-system-badge">{t('userGroups.systemBadge')}</span>}
            </h3>
            {desc && <p className="hr-gdrawer-head__desc">{desc}</p>}
            <div className="hr-gdrawer-head__stats">
              <span className="hr-chip hr-chip--sm" style={{ color: GROUP_COLOR, background: GROUP_COLOR + '15' }}>
                {Icons.team}
                {members.length} {members.length !== 1 ? t('userGroups.membersPlural') : t('userGroups.memberSingular')}
              </span>
              <span className="hr-chip hr-chip--sm hr-chip--success">
                <span className="hr-chip__dot" />
                {activeCount} {t('hrPanel.activeStatus')}
              </span>
            </div>
          </div>
          <div className="hr-gdrawer-head__actions">
            {!group.isSystem && (
              <>
                <button className="hr-icon-btn" onClick={onEdit} title={t('userGroups.editGroup')} aria-label={t('userGroups.editGroup')}>{Icons.edit}</button>
                <button className="hr-icon-btn hr-icon-btn--danger" onClick={onRequestDelete} title={t('userGroups.deleteGroup')} aria-label={t('userGroups.deleteGroup')}>{Icons.trash}</button>
              </>
            )}
            <button className="hr-icon-btn" onClick={onClose} aria-label={t('hrPanel.cancel')}>{Icons.x}</button>
          </div>
        </div>

        <div className="hr-drawer__body">
          <div className="hr-section">
            {/* Title row carries the primary "add" action so it can't be
                mistaken for a submit button of the filter box below. */}
            <div className="hr-gdrawer-sectionrow">
              <div className="hr-section__title">
                {Icons.team} {t('userGroups.members')}
                <span className="hr-gdrawer-count">{members.length}</span>
              </div>
              {!autoManaged && (
                <button
                  className="hr-btn hr-btn--primary hr-btn--sm"
                  onClick={() => { setShowAdd(v => !v); setUserSearch(''); }}
                >
                  {showAdd ? Icons.x : Icons.plus} {showAdd ? t('hrPanel.cancel') : t('userGroups.addMembers')}
                </button>
              )}
            </div>

            {autoManaged && (
              <div className="hr-alert hr-alert--info">
                {Icons.alert}<span>{t('userGroups.autoManagedHint')}</span>
              </div>
            )}

            {showAdd && (
              <div className="hr-picker">
                <div className="hr-search hr-picker__search">
                  <span className="hr-search__icon">{Icons.search}</span>
                  <input
                    className="hr-search__input"
                    autoFocus
                    placeholder={t('userGroups.searchUsersByNameOrEmail')}
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                  />
                  {userSearch && (
                    <button className="hr-search__clear" onClick={() => setUserSearch('')} aria-label={t('hrPanel.clearAll')}>{Icons.x}</button>
                  )}
                </div>
                <div className="hr-picker__list">
                  {addable.length === 0 ? (
                    <div className="hr-picker__empty">
                      {userSearch ? t('userGroups.noMatchingUsers') : t('userGroups.allUsersAreMembers')}
                    </div>
                  ) : (
                    addable.map(u => (
                      <button key={u._id} type="button" className="hr-member-row hr-member-row--btn" onClick={() => onAddMember(u._id)}>
                        <Avatar user={u} size={32} />
                        <div className="hr-member-row__info">
                          <span className="hr-member-row__name">{u.firstName} {u.lastName}</span>
                          <span className="hr-member-row__email">{u.email}</span>
                        </div>
                        <span className="hr-member-row__add">{Icons.plus}</span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}

            {members.length > 0 && (
              <div className="hr-group-tools">
                <div className="hr-search">
                  <span className="hr-search__icon">{Icons.search}</span>
                  <input
                    className="hr-search__input"
                    placeholder={t('userGroups.filterMembers')}
                    value={memberSearch}
                    onChange={e => setMemberSearch(e.target.value)}
                  />
                  {memberSearch && (
                    <button className="hr-search__clear" onClick={() => setMemberSearch('')} aria-label={t('hrPanel.clearAll')}>{Icons.x}</button>
                  )}
                </div>
              </div>
            )}

            {members.length === 0 ? (
              <div className="hr-empty">
                <div className="hr-empty__icon">{Icons.team}</div>
                <p className="hr-empty__text">{t('userGroups.noMembersYet')}</p>
                {!autoManaged && (
                  <button className="hr-btn hr-btn--primary" onClick={() => { setShowAdd(true); setUserSearch(''); }}>
                    {Icons.plus} {t('userGroups.addMembers')}
                  </button>
                )}
              </div>
            ) : visibleMembers.length === 0 ? (
              <div className="hr-empty">
                <div className="hr-empty__icon">{Icons.search}</div>
                <p className="hr-empty__text">{t('userGroups.noMatchingMembers')}</p>
                <button className="hr-btn hr-btn--ghost" onClick={() => setMemberSearch('')}>{t('hrPanel.clearAll')}</button>
              </div>
            ) : (
              <div className="hr-member-list">
                {visibleMembers.map(m => {
                  const id = m._id || m;
                  const isOn = m.isActive !== false;
                  return (
                    <div key={id} className="hr-member-row">
                      <Avatar user={m} size={36} />
                      <div className="hr-member-row__info">
                        <span className="hr-member-row__name">{`${m.firstName || ''} ${m.lastName || ''}`.trim() || m.email}</span>
                        <span className="hr-member-row__email">{m.email}</span>
                      </div>
                      <span className={`hr-chip hr-chip--sm hr-chip--${isOn ? 'success' : 'muted'}`}>
                        <span className="hr-chip__dot" />
                        {isOn ? t('hrPanel.activeStatus') : t('hrPanel.inactiveStatus')}
                      </span>
                      {!autoManaged && (
                        <button
                          className="hr-icon-btn hr-icon-btn--danger"
                          onClick={() => onRemoveMember(id)}
                          title={t('userGroups.removeFromGroup')}
                          aria-label={t('userGroups.removeFromGroup')}
                        >
                          {Icons.trash}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// User Groups Tab
// ═══════════════════════════════════════════════════════════════════════════
function GroupsTab({ allUsers, dispatch }) {
  const { t } = useTranslation();
  const { items: groups, selected, loading } = useSelector(s => s.groups);
  const [search, setSearch] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    dispatch(fetchGroups());
    // The member picker needs the full directory, not the HR default page.
    dispatch(fetchUsers({ limit: 500 }));
  }, [dispatch]);

  const filtered = useMemo(() => groups.filter(g =>
    !search ||
    (groupName(g, t) || '').toLowerCase().includes(search.toLowerCase()) ||
    (groupDesc(g, t) || '').toLowerCase().includes(search.toLowerCase())
  ), [groups, search, t]);

  const openGroup = (g) => {
    // Clear the previous selection so the drawer doesn't flash stale data
    // while the fresh (member-populated) group loads.
    dispatch(clearSelected());
    dispatch(fetchGroup(g._id));
    setDrawerOpen(true);
  };

  const handleSave = async (data) => {
    if (editGroup) {
      const r = await dispatch(updateGroup({ id: editGroup._id, data }));
      if (r.error) throw r.payload;
      toast.success(t('userGroups.messages.groupUpdated'));
    } else {
      const r = await dispatch(createGroup(data));
      if (r.error) throw r.payload;
      toast.success(t('userGroups.messages.groupCreated'));
      dispatch(fetchGroup(r.payload.group._id));
      setDrawerOpen(true);
    }
  };

  const handleDelete = async (g) => {
    try {
      await dispatch(deleteGroup(g._id)).unwrap();
      toast.success(t('userGroups.messages.groupDeleted'));
      setDrawerOpen(false);
    } catch (err) {
      toast.error(typeof err === 'string' ? err : t('userGroups.messages.failedToDelete'));
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleAddMember = async (userId) => {
    try {
      await dispatch(addGroupMembers({ groupId: selected._id, userIds: [userId] })).unwrap();
      toast.success(t('userGroups.messages.memberAdded'));
    } catch (err) {
      toast.error(typeof err === 'string' ? err : t('userGroups.messages.failedToAddMember'));
    }
  };

  const handleRemoveMember = async (userId) => {
    try {
      await dispatch(removeGroupMember({ groupId: selected._id, userId })).unwrap();
      toast.success(t('userGroups.messages.memberRemoved'));
    } catch (err) {
      toast.error(typeof err === 'string' ? err : t('userGroups.messages.failedToRemoveMember'));
    }
  };

  // Members may arrive as ids or populated docs — resolve for the counters.
  const resolveMembers = (g) => (g.members || [])
    .map(m => (typeof m === 'string' ? allUsers.find(u => u._id === m) : m))
    .filter(Boolean);

  return (
    <div className="hr__content" key="groups">
      <div className="hr-toolbar">
        <div className="hr-toolbar__left">
          <div className="hr-search">
            <span className="hr-search__icon">{Icons.search}</span>
            <input
              className="hr-search__input"
              placeholder={t('userGroups.searchGroups')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              aria-label={t('userGroups.searchGroups')}
            />
            {search && <button className="hr-search__clear" onClick={() => setSearch('')} aria-label={t('hrPanel.clearAll')}>{Icons.x}</button>}
          </div>
        </div>
        <div className="hr-toolbar__right">
          <button className="hr-btn hr-btn--primary" onClick={() => { setEditGroup(null); setShowModal(true); }}>
            {Icons.plus} {t('userGroups.newGroup')}
          </button>
        </div>
      </div>

      {loading && groups.length === 0 ? (
        <div className="hr-skeleton">
          {[...Array(4)].map((_, i) => <div key={i} className="hr-skeleton__row" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="hr-empty">
          <div className="hr-empty__icon">{Icons.team}</div>
          <p className="hr-empty__text">{search ? t('userGroups.noMatchingGroups') : t('userGroups.noGroupsYet')}</p>
          {!search && (
            <button className="hr-btn hr-btn--primary" onClick={() => { setEditGroup(null); setShowModal(true); }}>
              {Icons.plus} {t('userGroups.createFirstGroup')}
            </button>
          )}
        </div>
      ) : (
        <div className="hr-table-wrap">
          <table className="hr-table">
            <thead>
              <tr>
                <th>{t('hrPanel.columns.name')}</th>
                <th>{t('hrPanel.description')}</th>
                <th>{t('userGroups.members')}</th>
                <th>{t('hrPanel.active')}</th>
                <th>{t('hrPanel.columns.status')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(g => {
                const resolved = resolveMembers(g);
                const cnt = g.memberCount ?? (g.members || []).length;
                const activeCnt = resolved.filter(u => u.isActive !== false).length;
                return (
                  <tr key={g._id} className="hr-table__row" onClick={() => openGroup(g)} tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openGroup(g); } }}>
                    <td>
                      <div className="hr-table-primary">
                        <div className="hr-dot-icon" style={{ background: GROUP_COLOR + '15', color: GROUP_COLOR }}>{Icons.team}</div>
                        <strong>{groupName(g, t)}</strong>
                        {g.isSystem && <span className="hr-system-badge">{t('userGroups.systemBadge')}</span>}
                      </div>
                    </td>
                    <td className="hr-table__desc">{groupDesc(g, t) || '—'}</td>
                    <td>
                      <span className="hr-count-badge">
                        {Icons.team}
                        {cnt} {cnt !== 1 ? t('userGroups.membersPlural') : t('userGroups.memberSingular')}
                      </span>
                    </td>
                    <td>
                      <span className="hr-chip hr-chip--sm hr-chip--success">
                        <span className="hr-chip__dot" />
                        {activeCnt}
                      </span>
                    </td>
                    <td>
                      <span className={`hr-chip hr-chip--sm hr-chip--${g.isActive !== false ? 'success' : 'muted'}`}>
                        <span className="hr-chip__dot" />
                        {g.isActive !== false ? t('hrPanel.activeStatus') : t('hrPanel.inactiveStatus')}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {drawerOpen && selected && (
        <GroupDrawer
          group={selected}
          allUsers={allUsers}
          onClose={() => setDrawerOpen(false)}
          onEdit={() => { setEditGroup(selected); setShowModal(true); }}
          onRequestDelete={() => setDeleteConfirm(selected)}
          onAddMember={handleAddMember}
          onRemoveMember={handleRemoveMember}
        />
      )}

      {showModal && (
        <GroupModal
          group={editGroup}
          onClose={() => { setShowModal(false); setEditGroup(null); }}
          onSave={handleSave}
        />
      )}

      {deleteConfirm && (
        <div className="hr-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="hr-modal hr-modal--sm" onClick={e => e.stopPropagation()} role="alertdialog" aria-modal="true">
            <div className="hr-modal__header hr-modal__header--danger">
              <div className="hr-modal__header-icon">{Icons.alert}</div>
              <div className="hr-modal__header-text">
                <h2>{t('userGroups.deleteGroup')}</h2>
                <p>{t('userGroups.messages.deleteConfirm', { name: groupName(deleteConfirm, t) })}</p>
              </div>
              <button className="hr-modal__close" onClick={() => setDeleteConfirm(null)} aria-label={t('hrPanel.cancel')}>{Icons.x}</button>
            </div>
            <div className="hr-modal__footer">
              <button className="hr-btn hr-btn--ghost" onClick={() => setDeleteConfirm(null)}>{t('hrPanel.cancel')}</button>
              <button className="hr-btn hr-btn--danger" onClick={() => handleDelete(deleteConfirm)}>{t('userGroups.deleteGroup')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Main HR Panel
// ═══════════════════════════════════════════════════════════════════════════
export default function HRPanel() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { items: users, loading: usersLoading } = useSelector(s => s.users);
  const { items: departments, loading: deptsLoading } = useSelector(s => s.departments);
  const { items: companies, loading: companiesLoading } = useSelector(s => s.companies);
  const { items: groups } = useSelector(s => s.groups);
  // Active tab lives in the URL (?tab=departments) so the sidebar can
  // deep-link into a specific section and browser back/forward work.
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_TABS = ['users', 'departments', 'positions', 'titles', 'companies', 'groups'];
  const tabParam = searchParams.get('tab');
  const activeTab = VALID_TABS.includes(tabParam) ? tabParam : 'users';
  const setActiveTab = (id) => setSearchParams({ tab: id }, { replace: true });
  const [positions, setPositions] = useState([]);
  const [titles, setTitles] = useState([]);

  useEffect(() => {
    dispatch(fetchUsers({ limit: 100 }));
    dispatch(fetchDepartments());
    dispatch(fetchCompanies());
    dispatch(fetchGroups());
    positionsAPI.list({ active: true }).then(r => setPositions(r.data.positions || [])).catch(() => {});
    titlesAPI.list({ active: true }).then(r => setTitles(r.data.titles || [])).catch(() => {});
  }, [dispatch]);

  const activeUsers = useMemo(() => users.filter(u => u.isActive), [users]);

  // Tab labels reuse the sidebar's nav.* keys so the menu item, the page
  // title and the active tab can never diverge (one name everywhere).
  // Record counts live in the summary cards below — showing them on the tabs
  // too repeated the same figure twice on every screen.
  // Tab order mirrors the sidebar: frequently used records first, rarely
  // maintained master data (companies, groups) last.
  const tabs = [
    { id: 'users', label: t('nav.users', 'Users'), icon: Icons.users },
    { id: 'departments', label: t('nav.orgUnits', 'Units'), icon: Icons.building },
    { id: 'positions', label: t('nav.positions', 'Positions'), icon: Icons.briefcase },
    { id: 'titles', label: t('nav.titles', 'Titles'), icon: Icons.badge },
    { id: 'companies', label: t('nav.companies', 'Companies'), icon: Icons.company },
    { id: 'groups', label: t('nav.groups', 'User Groups'), icon: Icons.team },
  ];
  const activeTabDef = tabs.find(tb => tb.id === activeTab);

  // Every card counts ACTIVE records only — a passive record is not something
  // the organization currently has. Users had two cards (total + active) for
  // the same idea; they are now one.
  const countActive = (list) => (list || []).filter(x => x?.isActive !== false).length;

  const kpis = [
    { key: 'users', tone: 'accent', icon: Icons.users, value: activeUsers.length, label: t('nav.users', 'Users') },
    { key: 'depts', tone: 'info', icon: Icons.building, value: countActive(departments), label: t('nav.orgUnits', 'Units') },
    { key: 'pos', tone: 'warning', icon: Icons.briefcase, value: countActive(positions), label: t('nav.positions', 'Positions') },
    { key: 'titles', tone: 'info', icon: Icons.badge, value: countActive(titles), label: t('nav.titles', 'Titles') },
    { key: 'companies', tone: 'warning', icon: Icons.company, value: countActive(companies), label: t('nav.companies', 'Companies') },
    { key: 'groups', tone: 'accent', icon: Icons.team, value: countActive(groups), label: t('nav.groups', 'User Groups') },
  ];

  return (
    <div className="hr">
      {/* Standard page header — no bespoke HR hero card. Title mirrors the
          sidebar item that opened this tab; the parent menu name is the small
          group label above it. */}
      <PageHeader
        label={t('nav.hrPanel', 'Human Resources')}
        labelIcon={Icons.sparkle}
        title={activeTabDef?.label || t('hrPanel.title')}
        description={t(`pageDescriptions.${activeTab}`, { defaultValue: t('hrPanel.subtitle') })}
      >
        <div className="hr__kpis">
          {kpis.map(k => (
            <div key={k.key} className={`hr-kpi hr-kpi--${k.tone}`}>
              <div className="hr-kpi__rail" />
              <div className="hr-kpi__icon">{k.icon}</div>
              <div className="hr-kpi__body">
                <span className="hr-kpi__value">{k.value}</span>
                <span className="hr-kpi__label">{k.label}</span>
              </div>
            </div>
          ))}
        </div>
      </PageHeader>

      {/* Tab Dock */}
      <nav className="hr__nav" role="tablist" aria-label={t('hrPanel.title')}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`hr__nav-btn ${activeTab === tab.id ? 'is-active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="hr__nav-icon">{tab.icon}</span>
            <span className="hr__nav-label">{tab.label}</span>
          </button>
        ))}
      </nav>

      {/* Tab Content */}
      {activeTab === 'users' && (
        <UsersTab users={users} departments={departments} positions={positions} titles={titles} companies={companies} loading={usersLoading} dispatch={dispatch} />
      )}
      {activeTab === 'companies' && (
        <CompaniesTab companies={companies} loading={companiesLoading} dispatch={dispatch} />
      )}
      {activeTab === 'departments' && (
        <DepartmentsTab departments={departments} users={users} companies={companies} loading={deptsLoading} dispatch={dispatch} />
      )}
      {activeTab === 'positions' && (
        <PositionsTab users={users} companies={companies} />
      )}
      {activeTab === 'titles' && (
        <TitlesTab companies={companies} />
      )}
      {activeTab === 'groups' && (
        <GroupsTab allUsers={users} dispatch={dispatch} />
      )}
    </div>
  );
}
